import type { ReactNode } from 'react';
import posthogJs, { type PostHog } from 'posthog-js';
import { PostHogProvider, PostHogErrorBoundary } from '@posthog/react';
import useAuth from 'src/hooks/useAuth';

/**
 * Dual PostHog setup with tenant-aware routing.
 *
 * Two PostHog instances are initialised:
 *  - Cloud (default, unnamed)            → `VITE_POSTHOG_CLOUD_KEY`, proxied via `VITE_API_URL` + `/hedgehog`
 *  - Self-hosted (named `self_hosted`)   → `VITE_POSTHOG_KEY` / `VITE_POSTHOG_HOST`
 *
 * Routing rules:
 *  - Tenants flagged ``posthog_self_hosted_only`` MUST NOT send any data to
 *    PostHog Cloud. `usePostHog()` returns the self-hosted client for these
 *    tenants, and `setTenantSelfHostedOnly(true)` opts cloud out of capturing
 *    entirely so autocaptured events (e.g. `capture_pageleave`) don't leak.
 *  - All other tenants: cloud is primary; writes (capture/identify/reset)
 *    are also mirrored to self-hosted on a best-effort basis. Reads
 *    (feature flags) come from cloud only — cloud is the source of truth
 *    for feature-flag definitions.
 *
 * The `posthog_self_hosted_only` flag is a per-tenant DB column managed by
 * platform admins via the Organisation Management admin UI. It rides on
 * every tenant in the user's profile (`/account/info` ➜
 * `tenants[].posthog_self_hosted_only`) and is read off `useAuth()`.
 *
 * Boot-time leak prevention: on a refresh by a logged-in self-hosted-only
 * user, `useAuth()` resolves async. To close the window we read the cached
 * user blob from localStorage *before* the React tree mounts:
 *  - If the cache says self-hosted-only, we skip `posthog.init` for cloud
 *    entirely. The cloud singleton stays uninitialised — its initial
 *    `/decide` (feature-flag fetch) network call never fires, and
 *    `cloudClient` becomes a transparent no-op for cloud while still firing
 *    the self-hosted mirror.
 *  - Cloud is then initialised lazily on the first
 *    `setTenantSelfHostedOnly(false)` call, which covers the case where a
 *    cached self-hosted-only user logs out and a non-restricted user signs
 *    in without a page reload.
 */

const SELF_HOSTED_INSTANCE_NAME = 'self_hosted';

const CLOUD_KEY = import.meta.env.VITE_POSTHOG_CLOUD_KEY as string | undefined;
// the backend api url is used to proxy the posthog requests to the cloud to reduce the chance of blocking by ad/privacy blockers
const APP_API_URL = import.meta.env.VITE_API_URL as string | undefined;
const CLOUD_HOST = APP_API_URL ? `${APP_API_URL}/hedgehog` : undefined;
const SELF_HOSTED_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const SELF_HOSTED_HOST = import.meta.env.VITE_POSTHOG_HOST as string | undefined;
const IS_LOCALHOST = ['localhost', '127.0.0.1'].includes(location.hostname);

const sharedConfig = {
  debug: import.meta.env.MODE === 'development',
  defaults: '2025-05-24' as const,
  autocapture: false,
  // We capture pageviews manually in the AppProviders component
  // as by default posthog won't capture pageviews for SPAs
  capture_pageview: false,
  capture_pageleave: true
};

/**
 * Synchronously decide whether the cached user is self-hosted-only, by
 * reading the cached profile written on the previous session. Used to gate
 * cloud `posthog.init` so the initial `/decide` network call never reaches
 * cloud for returning self-hosted-only users.
 */
const readSelfHostedOnlyFromCache = (): boolean => {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const parsed = JSON.parse(raw) as {
      tenants?: Array<{ posthog_self_hosted_only?: boolean }>;
    };
    return isUserSelfHostedOnly({ tenants: parsed.tenants });
  } catch {
    return false;
  }
};

const cloud: PostHog = posthogJs;
let cloudInitialised = false;

const selfHosted = (): PostHog | undefined => {
  const candidate = (posthogJs as unknown as Record<string, unknown>)[SELF_HOSTED_INSTANCE_NAME];
  return typeof candidate === 'object' && candidate !== null ? (candidate as PostHog) : undefined;
};

const initialiseCloud = (): void => {
  if (cloudInitialised || !CLOUD_KEY) return;
  posthogJs.init(CLOUD_KEY, {
    ...sharedConfig,
    api_host: CLOUD_HOST,
    ui_host: 'https://eu.posthog.com'
  });
  cloudInitialised = true;
  if (IS_LOCALHOST) {
    cloud.opt_out_capturing();
  }
};

if (!readSelfHostedOnlyFromCache()) {
  initialiseCloud();
}

if (SELF_HOSTED_KEY) {
  posthogJs.init(
    SELF_HOSTED_KEY,
    {
      ...sharedConfig,
      api_host: SELF_HOSTED_HOST
    },
    SELF_HOSTED_INSTANCE_NAME
  );
  if (IS_LOCALHOST) {
    selfHosted()?.opt_out_capturing();
  }
}

/**
 * Toggle whether the cloud instance is allowed to send any data. Call with
 * `true` for self-hosted-only tenants. Call with `false` on logout / for
 * non-restricted tenants to resume cloud capture.
 *
 * Three posthog-js mechanisms are needed to fully silence cloud, because
 * `opt_out_capturing()` and feature-flag fetching go through different code
 * paths, and we also want to sever the local identity link:
 *  - `reset()` generates a fresh anonymous distinct_id on the cloud client
 *    and clears stored person properties. Server-side, any events already
 *    attached to the user's previous identity stay there — we don't try to
 *    delete that history. But any future stray autocapture from this device
 *    (e.g. autocaptured `$pageleave` that races opt-out) lands under the
 *    new anonymous id rather than under the user's named identity. Matters
 *    for users who joined a restricted tenant *after* already being known
 *    to cloud from a prior non-restricted session.
 *  - `opt_out_capturing()` blocks event sends (capture, identify,
 *    autocaptured `$pageleave`).
 *  - `advanced_disable_feature_flags` stops the periodic `/flags/` POST that
 *    posthog-js otherwise polls (with the user's distinct_id and context in
 *    the body) regardless of opt-out state.
 *
 * `usePostHog()` already routes new calls correctly based on the user's
 * tenant memberships, but FirebaseContext calls this *before* `identify()`
 * to defend against the in-render window where `useAuth()` hasn't yet
 * flipped to the new user, so even a `cloudClient.identify` call lands as a
 * no-op on cloud.
 *
 * Calling with `false` lazily initialises cloud if it wasn't initialised at
 * module load (because the cached tenant was self-hosted-only). This
 * supports a self-hosted-only user logging out and a non-restricted user
 * signing in without a full page reload.
 */
export const setTenantSelfHostedOnly = (selfHostedOnly: boolean): void => {
  if (selfHostedOnly) {
    if (cloudInitialised) {
      // Sever the local link to the user's identified cloud distinct_id.
      // Matters for users who became restricted *after* already being
      // identified on cloud (in a prior non-restricted session): without
      // reset, any post-opt-out stray event from this device would carry
      // their external_id. After reset the local distinct_id is a fresh
      // anonymous one, so future leaks (race conditions, regressions)
      // can't be linked back to their named identity. Existing
      // server-side events under the old external_id are untouched —
      // we're not trying to rewrite history, only stop new writes from
      // attaching to it. Fires once per transition: cache catches up on
      // the next refresh and skips cloud init entirely after that.
      cloud.reset();
      cloud.opt_out_capturing();
      cloud.set_config({ advanced_disable_feature_flags: true });
    }
  } else {
    initialiseCloud();
    if (cloudInitialised && !IS_LOCALHOST) {
      cloud.opt_in_capturing();
      cloud.set_config({ advanced_disable_feature_flags: false });
    }
  }
};

/**
 * Mirror a write to the self-hosted instance.
 *
 * For non-restricted tenants we keep cloud as the primary destination but
 * ALSO fire writes against the self-hosted instance, so we retain a copy of
 * event data we control end-to-end (data residency, our own retention,
 * independence from cloud-side outages or pricing changes). Self-hosted
 * failures must never break the primary code path — analytics is a side
 * concern and the calling component shouldn't crash because the secondary
 * collector is down.
 */
const mirrorToSelfHosted = (
  method: 'capture' | 'captureException' | 'identify' | 'reset' | 'register' | 'unregister',
  args: unknown[]
): void => {
  const sh = selfHosted();
  if (!sh) return;
  try {
    (sh[method] as (...a: unknown[]) => unknown).apply(sh, args);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(`[posthog:${SELF_HOSTED_INSTANCE_NAME}] ${method} failed`, err);
  }
};

/**
 * The subset of the PostHog API our codebase actually calls. Narrowing the
 * surface lets `usePostHog()` return either the cloud-with-mirror client or
 * the self-hosted-only client interchangeably, and gives a TypeScript error
 * if a future caller reaches for a method that hasn't been routed.
 */
export type PostHogClient = Pick<
  PostHog,
  | 'capture'
  | 'captureException'
  | 'identify'
  | 'reset'
  | 'isFeatureEnabled'
  | 'getFeatureFlag'
  | 'onFeatureFlags'
  | 'register'
  | 'unregister'
>;

const cloudClient: PostHogClient = {
  capture: (...args: Parameters<PostHog['capture']>) => {
    const result = cloudInitialised ? cloud.capture(...args) : undefined;
    mirrorToSelfHosted('capture', args);
    return result;
  },
  captureException: (...args: Parameters<PostHog['captureException']>) => {
    const result = cloudInitialised ? cloud.captureException(...args) : undefined;
    mirrorToSelfHosted('captureException', args);
    return result;
  },
  identify: (...args: Parameters<PostHog['identify']>) => {
    if (cloudInitialised) cloud.identify(...args);
    mirrorToSelfHosted('identify', args);
  },
  reset: (...args: Parameters<PostHog['reset']>) => {
    if (cloudInitialised) cloud.reset(...args);
    mirrorToSelfHosted('reset', args);
  },
  isFeatureEnabled: (...args: Parameters<PostHog['isFeatureEnabled']>) =>
    cloudInitialised ? cloud.isFeatureEnabled(...args) : undefined,
  getFeatureFlag: (...args: Parameters<PostHog['getFeatureFlag']>) =>
    cloudInitialised ? cloud.getFeatureFlag(...args) : undefined,
  onFeatureFlags: (...args: Parameters<PostHog['onFeatureFlags']>) =>
    cloudInitialised ? cloud.onFeatureFlags(...args) : () => undefined,
  register: (...args: Parameters<PostHog['register']>) => {
    if (cloudInitialised) cloud.register(...args);
    mirrorToSelfHosted('register', args);
  },
  unregister: (...args: Parameters<PostHog['unregister']>) => {
    if (cloudInitialised) cloud.unregister(...args);
    mirrorToSelfHosted('unregister', args);
  }
};

const selfHostedClient: PostHogClient = {
  capture: (...args: Parameters<PostHog['capture']>) => selfHosted()?.capture(...args),
  captureException: (...args: Parameters<PostHog['captureException']>) => selfHosted()?.captureException(...args),
  identify: (...args: Parameters<PostHog['identify']>) => {
    selfHosted()?.identify(...args);
  },
  reset: (...args: Parameters<PostHog['reset']>) => {
    selfHosted()?.reset(...args);
  },
  isFeatureEnabled: (...args: Parameters<PostHog['isFeatureEnabled']>) => selfHosted()?.isFeatureEnabled(...args),
  getFeatureFlag: (...args: Parameters<PostHog['getFeatureFlag']>) => selfHosted()?.getFeatureFlag(...args),
  onFeatureFlags: (...args: Parameters<PostHog['onFeatureFlags']>) =>
    selfHosted()?.onFeatureFlags(...args) ?? (() => undefined),
  register: (...args: Parameters<PostHog['register']>) => {
    selfHosted()?.register(...args);
  },
  unregister: (...args: Parameters<PostHog['unregister']>) => {
    selfHosted()?.unregister(...args);
  }
};

/**
 * Decide whether a user must be routed to self-hosted only.
 *
 * Tenant-membership-based: if ANY tenant the user belongs to is flagged
 * ``posthog_self_hosted_only`` we treat the user as self-hosted-only for the
 * whole session. We deliberately don't switch routing based on the active
 * tenant — the user's identity is the same across tenants, and once their
 * distinct_id has touched cloud it's leaked. Erring towards "restricted" is
 * the correct failure mode for data residency.
 */
export function isUserSelfHostedOnly(
  user: { tenants?: Array<{ posthog_self_hosted_only?: boolean }> } | null | undefined
): boolean {
  if (!user) return false;
  return user.tenants?.some((t) => t.posthog_self_hosted_only) ?? false;
}

/**
 * Returns a PostHog client that respects the user's data-residency setting.
 * Always go through this hook — direct imports of `posthog-js` /
 * `posthog-js/react` bypass tenant routing and are blocked by ESLint.
 */
export function usePostHog(): PostHogClient {
  const { user } = useAuth();
  return isUserSelfHostedOnly(user) ? selfHostedClient : cloudClient;
}

/**
 * Escape hatch for code that needs the raw cloud singleton (e.g. wiring up
 * the dev toolbar via `window.posthog`). Do NOT use this for tenant-scoped
 * operations — go through `usePostHog()` instead.
 */
export const cloudPosthog = cloud;

/**
 * App-level error boundary that reports uncaught render errors to PostHog
 * error tracking (`@posthog/react`). It is disabled on localhost so local
 * development errors never reach either PostHog instance.
 *
 * Deliberately always routes to the cloud client, regardless of a tenant's
 * `posthog_self_hosted_only` setting — unlike `usePostHog()`, this doesn't
 * yet respect tenant-aware routing. It's wired here rather than at the call
 * site so the app never imports `@posthog/react` directly (see the
 * `no-restricted-imports` ESLint rule).
 *
 * `PostHogProvider` is given the already-initialised `cloud` singleton via
 * `client`, so it only supplies React context here — it doesn't re-init
 * PostHog. If cloud hasn't been initialised (e.g. a cached
 * self-hosted-only user, see the module docstring), captured exceptions
 * are silently dropped rather than raising.
 */
export const AppPostHogErrorBoundary = ({ children, fallback }: { children: ReactNode; fallback: ReactNode }) => {
  if (IS_LOCALHOST) {
    return children;
  }

  return (
    <PostHogProvider client={cloud}>
      <PostHogErrorBoundary fallback={fallback}>{children}</PostHogErrorBoundary>
    </PostHogProvider>
  );
};

/**
 * The cloud PostHog session id for the current browser session, or
 * `undefined` if cloud hasn't been initialised (e.g. a cached
 * self-hosted-only user — see the module docstring).
 *
 * Used by the axios client to attach an `X-POSTHOG-SESSION-ID` header to
 * API requests, so backend-side `capture_exception` calls (which always
 * report to PostHog cloud, see `logging_utils.py`) can be linked back to
 * the browser session that triggered them. Deliberately always the cloud
 * session id, matching where the backend reports errors, regardless of the
 * requesting user's self-hosted-only routing — the backend doesn't know the
 * user at the point it captures an unhandled exception.
 */
export const getCloudSessionId = (): string | undefined => {
  if (!cloudInitialised) return undefined;
  try {
    return cloud.get_session_id();
  } catch {
    return undefined;
  }
};

/**
 * The cloud PostHog distinct id for the current browser, or `undefined` if
 * cloud hasn't been initialised. Attached as `X-POSTHOG-DISTINCT-ID` so
 * backend-side `capture_exception` calls can attribute errors to the right
 * PostHog person even before the backend can resolve an authenticated user
 * (e.g. on login/signup), and errors get merged into the person's timeline
 * once `identify()` runs. The backend prefers its own auth-resolved
 * distinct id over this header once a user is authenticated (see
 * `get_any_user` in `auth/utils.py`) — this is the anonymous-friendly
 * fallback, not the source of truth for authenticated requests.
 */
export const getCloudDistinctId = (): string | undefined => {
  if (!cloudInitialised) return undefined;
  try {
    return cloud.get_distinct_id();
  } catch {
    return undefined;
  }
};
